import { useContext, useMemo } from 'react';
import { DataContext } from '../../Services/Context-Api/Context';

/**
 * Custom hook to get auth information
 * Returns: { user, isAuthenticated, loading, userRole, permissions }
 * 
 * Also checks localStorage as a fallback to ensure we always have current state,
 * especially after logout or token refresh events
 */
const useAuth = () => {
  const context = useContext(DataContext);

  if (!context) {
    throw new Error('useAuth must be used within DataProvider');
  }

  const {
    user = null,
    isAuthenticated = false,
    userRole = null,
    permissions = [],
    loading = false,
  } = context;

  // Memoize the auth state, checking both context and localStorage
  const authState = useMemo(() => {
    // Primary source: context state
    let authenticated = isAuthenticated;
    let currentUser = user;
    let currentRole = userRole;
    let currentPermissions = permissions;

    // Fallback: check localStorage if context indicates no auth
    if (!authenticated) {
      try {
        const userData = localStorage.getItem('User');
        const authentication = localStorage.getItem('Authentication');
        
        if (userData && authentication) {
          const parsedUser = JSON.parse(userData);
          authenticated = true;
          currentUser = parsedUser;
          currentRole = parsedUser?.role || null;
          
          const permissionsData = localStorage.getItem('Permissions');
          if (permissionsData) {
            currentPermissions = JSON.parse(permissionsData);
          } else {
            currentPermissions = parsedUser?.permissions || [];
          }
        }
      } catch (e) {
        console.error('Error checking localStorage in useAuth:', e);
      }
    }

    return {
      user: currentUser,
      isAuthenticated: authenticated,
      userRole: currentRole,
      permissions: currentPermissions,
      loading,
    };
  }, [isAuthenticated, user, userRole, permissions, loading]);

  return authState;
};

export default useAuth;
